/**
 * QConversationHistory Component (Task 4.4)
 * Dropdown panel for browsing past conversations
 *
 * Features:
 * - Dropdown menu triggered by clock/history icon
 * - Lists past conversations with title/preview
 * - Click to load conversation
 * - Delete conversation option
 * - Compact dark theme matching Bolt/Claude style
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Fade,
  ClickAwayListener
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

/**
 * Format relative time (e.g., "2h ago", "Yesterday", "Dec 8")
 */
const formatRelativeTime = (dateString) => {
  if (!dateString) return '';

  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;

  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

/**
 * Truncate text with ellipsis
 */
const truncateText = (text, maxLength = 40) => {
  if (!text) return '';
  return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
};

/**
 * QConversationHistory Component
 * @param {Array} conversations - List of conversations from API
 * @param {boolean} isLoading - Loading state
 * @param {string} currentConversationId - Currently active conversation ID
 * @param {function} onSelectConversation - Callback when conversation selected
 * @param {function} onDeleteConversation - Callback when conversation deleted
 * @param {function} onRefresh - Callback to refresh conversation list
 */
const QConversationHistory = ({
  conversations = [],
  isLoading = false,
  currentConversationId = null,
  onSelectConversation,
  onDeleteConversation,
  onRefresh
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const dropdownRef = useRef(null);

  // Stable ref for onRefresh to prevent infinite loops
  // (parent may not memoize the callback)
  const onRefreshRef = useRef(onRefresh);
  onRefreshRef.current = onRefresh;

  // Refresh list when dropdown opens (using ref to avoid dependency issues)
  useEffect(() => {
    if (isOpen && onRefreshRef.current) {
      onRefreshRef.current();
    }
  }, [isOpen]); // Only depend on isOpen, not onRefresh

  const handleToggle = () => {
    setIsOpen(prev => !prev);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = (conversationId) => {
    if (conversationId !== currentConversationId) {
      onSelectConversation?.(conversationId);
    }
    setIsOpen(false);
  };

  const handleDelete = async (e, conversationId) => {
    e.stopPropagation(); // Prevent selecting the conversation

    if (deletingId) return; // Prevent double-delete

    setDeletingId(conversationId);
    try {
      await onDeleteConversation?.(conversationId);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <ClickAwayListener onClickAway={handleClose}>
      <Box sx={{ position: 'relative' }} ref={dropdownRef}>
        {/* Trigger Button - Polished, Bright, User-Friendly */}
        <Tooltip
          title="View past conversations"
          arrow
          placement="bottom"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#1F1F23',
                color: '#E4E4E7',
                fontSize: '0.75rem',
                fontWeight: 500,
                border: '1px solid rgba(255,255,255,0.1)',
                '& .MuiTooltip-arrow': {
                  color: '#1F1F23'
                }
              }
            }
          }}
        >
          <IconButton
            onClick={handleToggle}
            size="small"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              borderRadius: '8px',
              padding: '6px 10px',
              backgroundColor: isOpen
                ? 'rgba(124, 58, 237, 0.15)'
                : 'rgba(124, 58, 237, 0.08)',
              border: isOpen
                ? '1px solid rgba(124, 58, 237, 0.3)'
                : '1px solid rgba(124, 58, 237, 0.15)',
              color: isOpen ? '#A78BFA' : '#8B5CF6',
              transition: 'all 0.2s ease',
              '&:hover': {
                backgroundColor: 'rgba(124, 58, 237, 0.15)',
                borderColor: 'rgba(124, 58, 237, 0.3)',
                color: '#A78BFA',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 12px rgba(124, 58, 237, 0.2)'
              },
              '&:active': {
                transform: 'translateY(0)',
              }
            }}
          >
            <HistoryIcon sx={{ fontSize: '1rem' }} />
            <Typography
              sx={{
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.01em',
                display: { xs: 'none', sm: 'block' }
              }}
            >
              History
            </Typography>
          </IconButton>
        </Tooltip>

        {/* Dropdown Panel - Enhanced styling */}
        <Fade in={isOpen} timeout={150}>
          <Box
            sx={{
              position: 'absolute',
              top: '100%',
              right: 0,
              marginTop: '8px',
              width: '300px',
              maxHeight: '400px',
              backgroundColor: '#131316',
              border: '1px solid rgba(124, 58, 237, 0.15)',
              borderRadius: '12px',
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.03)',
              overflow: 'hidden',
              zIndex: 1000,
              display: isOpen ? 'block' : 'none'
            }}
          >
            {/* Header - Enhanced */}
            <Box
              sx={{
                padding: '14px 16px',
                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'linear-gradient(180deg, rgba(124, 58, 237, 0.05) 0%, transparent 100%)'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HistoryIcon sx={{ fontSize: '1rem', color: '#8B5CF6' }} />
                <Typography
                  sx={{
                    fontSize: '0.8125rem',
                    fontWeight: 600,
                    color: '#E4E4E7',
                    letterSpacing: '0.01em'
                  }}
                >
                  Past Conversations
                </Typography>
              </Box>
              {isLoading && (
                <CircularProgress size={14} sx={{ color: '#8B5CF6' }} />
              )}
            </Box>

            {/* Conversation List */}
            <Box
              sx={{
                maxHeight: '320px',
                overflowY: 'auto',
                padding: '4px',
                '&::-webkit-scrollbar': {
                  width: '6px'
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(124, 58, 237, 0.2)',
                  borderRadius: '3px',
                  '&:hover': {
                    background: 'rgba(124, 58, 237, 0.3)'
                  }
                }
              }}
            >
              {/* Empty State - Enhanced */}
              {!isLoading && conversations.length === 0 && (
                <Box
                  sx={{
                    padding: '32px 20px',
                    textAlign: 'center'
                  }}
                >
                  <Box
                    sx={{
                      width: 56,
                      height: 56,
                      borderRadius: '12px',
                      background: 'rgba(124, 58, 237, 0.08)',
                      border: '1px solid rgba(124, 58, 237, 0.15)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      margin: '0 auto 12px'
                    }}
                  >
                    <ChatBubbleOutlineIcon
                      sx={{
                        fontSize: '1.5rem',
                        color: '#8B5CF6'
                      }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      color: '#A1A1AA',
                      fontWeight: 600
                    }}
                  >
                    No conversations yet
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.8125rem',
                      color: '#52525B',
                      marginTop: '6px',
                      lineHeight: 1.4
                    }}
                  >
                    Start chatting with Q to build your history
                  </Typography>
                </Box>
              )}

              {/* Conversation Items */}
              {conversations.map((conv) => (
                <ConversationItem
                  key={conv.conversationId}
                  conversation={conv}
                  isActive={conv.conversationId === currentConversationId}
                  isDeleting={deletingId === conv.conversationId}
                  onSelect={() => handleSelect(conv.conversationId)}
                  onDelete={(e) => handleDelete(e, conv.conversationId)}
                />
              ))}
            </Box>
          </Box>
        </Fade>
      </Box>
    </ClickAwayListener>
  );
};

/**
 * Individual Conversation Item - Enhanced styling
 */
const ConversationItem = ({
  conversation,
  isActive,
  isDeleting,
  onSelect,
  onDelete
}) => {
  const [isHovered, setIsHovered] = useState(false);

  // Display title or fallback to first message preview
  const displayTitle = conversation.title ||
    truncateText(conversation.lastMessage) ||
    'Untitled conversation';

  return (
    <Box
      onClick={onSelect}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        padding: '12px 14px',
        margin: '2px 0',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: '10px',
        borderRadius: '8px',
        backgroundColor: isActive
          ? 'rgba(124, 58, 237, 0.12)'
          : isHovered
            ? 'rgba(255, 255, 255, 0.04)'
            : 'transparent',
        border: isActive
          ? '1px solid rgba(124, 58, 237, 0.25)'
          : '1px solid transparent',
        transition: 'all 0.15s ease',
        '&:hover': {
          backgroundColor: isActive
            ? 'rgba(124, 58, 237, 0.15)'
            : 'rgba(255, 255, 255, 0.04)'
        }
      }}
    >
      {/* Content */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <Typography
          sx={{
            fontSize: '0.8125rem',
            fontWeight: isActive ? 600 : 500,
            color: isActive ? '#A78BFA' : '#E4E4E7',
            lineHeight: 1.4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {displayTitle}
        </Typography>

        {/* Meta: time + message count */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '4px'
          }}
        >
          <Typography
            sx={{
              fontSize: '0.75rem',
              color: isActive ? '#8B5CF6' : '#71717A',
              fontWeight: 400
            }}
          >
            {formatRelativeTime(conversation.updatedAt || conversation.createdAt)}
          </Typography>
          {conversation.messageCount > 0 && (
            <>
              <Box
                sx={{
                  width: 3,
                  height: 3,
                  borderRadius: '50%',
                  backgroundColor: '#52525B'
                }}
              />
              <Typography
                sx={{
                  fontSize: '0.75rem',
                  color: '#71717A',
                  fontWeight: 400
                }}
              >
                {conversation.messageCount} messages
              </Typography>
            </>
          )}
        </Box>
      </Box>

      {/* Delete Button - Show on hover */}
      <Fade in={isHovered && !isActive}>
        <IconButton
          onClick={onDelete}
          disabled={isDeleting}
          size="small"
          sx={{
            padding: '6px',
            borderRadius: '6px',
            color: '#71717A',
            opacity: isHovered && !isActive ? 1 : 0,
            transition: 'all 0.15s ease',
            '&:hover': {
              color: '#EF4444',
              backgroundColor: 'rgba(239, 68, 68, 0.1)'
            }
          }}
        >
          {isDeleting ? (
            <CircularProgress size={14} sx={{ color: '#71717A' }} />
          ) : (
            <DeleteOutlineIcon sx={{ fontSize: '1rem' }} />
          )}
        </IconButton>
      </Fade>
    </Box>
  );
};

export default QConversationHistory;
